<template>
  <b-modal id="view-modal" ref="modal" class="large-modal" size="xl">
    <template #modal-header>
      <div class="upper">
        <label>Student Details</label>
        <div v-if="student" class="new-title">
          {{ student.name }}
        </div>
      </div>
      <button class="close" @click="hide()">×</button>
    </template>

    <template v-slot:modal-footer>
      <button class="btn" type="button" @click="hide()">Dismiss</button>

      <button
        v-if="student"
        :disabled="updating"
        class="btn"
        type="button"
        @click="promptUser()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Send New User Prompts</span>
      </button>

      <button
        v-if="student"
        class="btn"
        :disabled="resetting"
        type="button"
        @click="resetPassword()"
      >
        <font-awesome-icon
          v-if="resetting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Reset Password</span>
      </button>

      <button
        v-if="student && student._is_active"
        :disabled="updating"
        class="btn"
        type="button"
        @click="disableUser()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Disable User</span>
      </button>

      <button
        v-if="student && student._is_active"
        :disabled="updating"
        class="btn"
        type="button"
        @click="convertToCounselor()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Convert To Counselor</span>
      </button>

      <button
        v-if="student && !student._is_active"
        :disabled="updating"
        class="btn"
        type="button"
        @click="enableUser()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Enable User</span>
      </button>

      <button v-if="student" class="btn" type="button" @click="viewProfile()">
        View Profile
      </button>
    </template>
  </b-modal>
</template>

<script>
import AdminClient from '@/api/AdminClient';
import UserClient from '@/api/UserClient';
import PromptsClient from '@/api/PromptsClient';

export default {
  name: 'ViewStudentModal',

  props: {
    reloadStudents: {
      type: Function,
      required: true
    },
  },

  data() {
    return {
      adminClient: new AdminClient(this.$http),
      userClient: new UserClient(this.$http),
      promptsClient: new PromptsClient(this.$http),

      student: null,

      resetting: false,
      updating: false
    };
  },

  methods: {
    show(student) {
      this.student = student;
      this.$refs.modal.show();
    },

    hide() {
      this.$refs.modal.hide();
    },
    async promptUser() {
      try {
        this.updating = true;
        await this.promptsClient.createPrompt(this.student.id, "CreditExplanationModal");
        await this.promptsClient.createPrompt(this.student.id, "CampusPoliciesModal");
        this.$store.commit('setGlobalAlert', {
          msg: 'Prompts have been set for user.'
        });
        this.hide();
      } catch(ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed setting prompts. Try again.',
          error: true
        });
      } finally {
        this.updating = false;
      }
    },
    async resetPassword() {
      this.resetting = true;
      try {
        await this.userClient.forgotPassword(this.student.email);
        this.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Student has been sent a link to reset their password.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to reset password. Please try again!',
          error: true
        });
      }
      this.resetting = false;
    },

    async disableUser() {
      this.updating = true;
      try {
        await this.adminClient.deactivateStudentUser(this.student.id);
        this.hide();
        this.reloadStudents();
        this.$store.commit('setGlobalAlert', {
          msg: 'Student has been disabled.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to disable student. Please try again!',
          error: true
        });
      }
      this.updating = false;
    },

    async convertToCounselor(){
      this.updating = true;
      try {
        await this.adminClient.convertToCounselor(this.student.id);
        this.hide();
        this.reloadStudents();
        this.$store.commit('setGlobalAlert', {
          msg: 'Student has been converted to Counselor.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to convert student to counselor. They may have existing sessions. Otherwise, try again!',
          error: true
        });
      }
      this.updating = false;
    },

    async enableUser() {
      this.updating = true;
      try {
        await this.adminClient.activateStudentUser(this.student.id);
        this.hide();
        this.reloadStudents();
        this.$store.commit('setGlobalAlert', {
          msg: 'Student has been enabled.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to enable student. Please try again!',
          error: true
        });
      }
      this.updating = false;
    },

    viewProfile() {
      this.$router.push({
        name: 'view-student',
        params: { id: this.student.id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
#view-modal {
  .upper {
    display: block;
    position: relative;
    padding-right: 30px;
  }

  label {
    @include label;

    display: block;
    margin-bottom: 10px;
  }

  .date {
    font-size: 18px;
    color: rgba(white, 0.75);
  }

  .pills {
    margin-top: 20px;
  }

  .new-title {
    display: block;
    color: white;
    font-size: 40px;
    font-weight: 700;
    font-family: 'Playfair Display', serif;
    line-height: 1;
  }

  .description {
    opacity: 0.6;
  }

  h6 {
    font-weight: normal !important;
    font-size: 25px;
    margin-top: 30px;
    margin-bottom: 10px;

    span {
      font-weight: bold !important;
    }
  }
}
</style>
